import { NgComponentOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { VxButtonComponent } from '../button/button.component';
import { VxIconComponent } from '../icon/icon.component';
import { VxIconButtonComponent } from '../icon-button/icon-button.component';
import { VxIconName } from '../icons';
import { VxMenuComponent } from '../menu/menu.component';
import { VxMenuItemComponent } from '../menu/menu-item.component';
import { VxMenuLabelComponent } from '../menu/menu-label.component';
import { VxMenuSeparatorComponent } from '../menu/menu-separator.component';
import { VxMenuTriggerDirective } from '../menu/menu-trigger.directive';
import { VxTooltipDirective } from '../tooltip/tooltip.directive';
import { toastVariants } from './toast.variants';
import { VxToastData, VxToastMenuItem, VxToastTone } from './toast.types';

const TONE_ICONS: Record<VxToastTone, VxIconName> = {
  info: 'info',
  success: 'check-circle',
  warning: 'warning',
  danger: 'x-circle',
};

/**
 * One notification card in three rows, all aligned to the title column:
 *
 *   [tile] Title                          [menu] [×]
 *          description / consumer `content`
 *          hint                            [action]
 *
 * A toast with a `duration` shows a countdown bar along its bottom edge.
 * Purely presentational; `VxToastService` and `<vx-toast-stack>` own the
 * queue and the timing — the stack passes each `VxToastEntry` as is.
 */
@Component({
  selector: 'vx-toast',
  templateUrl: './toast.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgComponentOutlet,
    VxButtonComponent,
    VxIconButtonComponent,
    VxIconComponent,
    VxMenuComponent,
    VxMenuItemComponent,
    VxMenuLabelComponent,
    VxMenuSeparatorComponent,
    VxMenuTriggerDirective,
    VxTooltipDirective,
  ],
  host: { role: 'status', '[class]': 'classes().root' },
})
export class VxToastComponent {
  readonly tone = input<VxToastTone>('info');

  /** What to show. The action's and menu items' handlers aren't called
   *  here — the toast emits `actionPressed` / `menuItemSelected` and the
   *  owner runs them. `duration` is animated as the countdown bar. */
  readonly toast = input.required<VxToastData>();

  /** Header row only — for toasts peeking out behind the front one, so a
   *  taller older toast can't show its body below a shorter newer one. */
  readonly compact = input(false);

  /** Freezes the countdown bar (the service pauses the real timer). */
  readonly paused = input(false);

  readonly actionPressed = output<void>();

  readonly menuItemSelected = output<VxToastMenuItem>();

  readonly menuOpened = output<void>();

  readonly menuClosed = output<void>();

  readonly dismissed = output<void>();

  protected readonly tileIcon = computed<VxIconName>(
    () => this.toast().icon ?? TONE_ICONS[this.tone()]
  );

  // tv() slots are functions — resolve them to strings before binding
  protected readonly classes = computed(() => {
    const slots = toastVariants({ tone: this.tone() });
    return { root: slots.root(), tile: slots.tile() };
  });
}
